import { createReducer } from 'typesafe-actions';
import {
    IOperation, EOperationStatus
} from 'types';
import { ICaptchaRequestResponse } from 'api/app';
import { IComparisonOperationValue, getDefaultServiceState } from '../objects-comparison/objects-comparison-service';
import { operationsActions as A, TOperationsActions } from './operations-reducer-actions';
import { IMapStateOperationData, defaultMapState } from '../map/map-service';
import { IFiltersOperationData } from '../containers/containers-service';
import { IFolderStateOperationData } from '../app/app-service';

/* eslint-disable-next-line max-len */
export const createOperation = <Data, Meta>(id: string, data: Data = null, meta: Meta = null): IOperation<Data, Meta> => ({
    id,
    status: EOperationStatus.UNKNOWN,
    error: null,
    error_message: null,
    data,
    meta
});

export interface IOperationsState {
    'MAP_STATE': IOperation<IMapStateOperationData>;
    'CONTAINERS': IOperation<omjs.IContainer[]>;
    'OBJECTS_COMPARISON': IOperation<IComparisonOperationValue>;
    'FILTERS': IOperation<IFiltersOperationData>;
    'DATA_LOAD': IOperation<null>
    'FILTERED_IDS': IOperation<{ [key: string]: number[] }>
    'FOLDER_STATE': IOperation<IFolderStateOperationData>;
    'CAPTCHA': IOperation<ICaptchaRequestResponse>
    [id: string]: IOperation;
}

const defaultState: IOperationsState = {
    'MAP_STATE': createOperation('MAP_STATE', defaultMapState),
    'CONTAINERS': createOperation('CONTAINERS', []),
    'OBJECTS_COMPARISON': createOperation('OBJECTS_COMPARISON', getDefaultServiceState()),
    'FILTERS': createOperation('FILTERS', {}),
    'DATA_LOAD': createOperation('DATA_LOAD', null),
    'FILTERED_IDS': createOperation('FILTERED_IDS', {}),
    'FOLDER_STATE': createOperation('FOLDER_STATE', {}),
    'CAPTCHA': createOperation('CAPTCHA', { SUCCESS: false })
};

export const operationsReducer = createReducer<IOperationsState, TOperationsActions>(defaultState)
    .handleAction(A.init, (state, action) => ({
        ...state,
        [action.payload]: createOperation(action.payload)
    }))
    .handleAction(A.pending, (state, action) => ({
        ...state,
        [action.payload]: {
            ...state[action.payload],
            status: EOperationStatus.PENDING,
            error: null,
            error_message: null
        }
    }))
    .handleAction(A.success, (state, action) => ({
        ...state,
        [action.payload]: {
            ...state[action.payload],
            status: EOperationStatus.SUCCESS,
            error: null,
            error_message: null
        }
    }))
    .handleAction(A.fail, (state, action) => ({
        ...state,
        [action.payload.id]: {
            ...state[action.payload.id],
            status: EOperationStatus.FAIL,
            error: action.payload.error,
            error_message: action.payload.error_message
        }
    }))
    .handleAction(A.clear, (state, action) => ({
        ...state,
        [action.payload]: {
            ...state[action.payload],
            status: EOperationStatus.UNKNOWN,
            error: null,
            data: null,
            meta: null,
            error_message: null
        }
    }))
    .handleAction(A.setMeta, (state, action) => ({
        ...state,
        [action.payload.id]: {
            ...state[action.payload.id],
            meta: action.payload.meta
        }
    }))
    .handleAction(A.setData, (state, action) => ({
        ...state,
        [action.payload.id]: {
            ...state[action.payload.id],
            data: action.payload.data
        }
    }));
